import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { availabilityShape, bibShape, catalogBibShape } from '@bibliocommons/bc-prop-types';
import { bindAll } from '@bibliocommons/utils-react';
import { FORMAT_CHOOSER_MODAL } from 'app/constants/BibConstants';
import FormatChooserCapsule from './FormatChooserCapsule';
import MoreFormatsDropdown from './MoreFormatsDropdown';
import FormatChooserModal from './FormatChooserModal';

import './FormatChooser.scss';

export class FormatChooser extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  createUiComponentLabel(bib) {
    const { catalogBib, intl } = this.props;

    const bibAvailability = bib.get('availability');
    const bibFormat = bib.getIn(['briefInfo', 'format']) ?? '';
    const bibPublicationDate = bib.getIn(['briefInfo', 'publicationDate']);
    const isMainBib = bib.get('id') === catalogBib.get('id');

    let uiComponentLabel = intl.formatMessage({ id: `FORMAT.${bibFormat.toLowerCase()}` });

    if (isMainBib) {
      uiComponentLabel += bibPublicationDate ? `, ${bibPublicationDate}` : '';
    }

    if (bibAvailability) {
      const location = bibAvailability.get('availabilityLocationType');
      const rollUpStatus = bibAvailability.get('statusType');
      const status = bibAvailability.get('localisedStatus');

      uiComponentLabel += ` | ${intl.formatMessage(
        {
          id: `circ_${status ? status.toLowerCase() : rollUpStatus.toLowerCase()}`
        },
        { location }
      )}`;
    }

    return uiComponentLabel;
  }

  handleOpen(e) {
    e.preventDefault();
    this.props.modalActions.openModal(FORMAT_CHOOSER_MODAL, { authRequired: false });
  }

  handleClose() {
    this.props.modalActions.closeModal(FORMAT_CHOOSER_MODAL);
  }

  handleEditionClick(edition) {
    const { trackHoldingsExploreEdition } = this.props;

    const bibId = edition.get('id');
    const uiComponentLabel = this.createUiComponentLabel(edition);

    trackHoldingsExploreEdition({
      bibId,
      ui: {
        ui_component_label: uiComponentLabel,
        ui_component_type: 'link-text'
      }
    });
  }

  handleManifestationClick(manifestation) {
    const { trackHoldingsExploreFormat } = this.props;

    const bibId = manifestation.get('id');
    const uiComponentLabel = this.createUiComponentLabel(manifestation);

    trackHoldingsExploreFormat({
      bibId,
      ui: {
        ui_component_label: uiComponentLabel,
        ui_component_type: 'link-text'
      }
    });
  }

  renderMoreFormatsDropdown() {
    if (this.props.manifestations.size > 3) {
      return (
        <MoreFormatsDropdown
          manifestations={this.props.manifestations.slice(3)}
          onManifestationClick={this.handleManifestationClick}
        />
      );
    }

    return null;
  }

  renderMobile() {
    const { manifestations, realtimeAvailabilities, catalogBib, summary } = this.props;
    const mainBib = manifestations.find(manifestation => manifestation.get('id') === catalogBib.get('id'));
    const mainBibAvailability = realtimeAvailabilities.get(mainBib.get('id'), mainBib.get('availability'));
    const formatsCount = manifestations.size;
    const multipleManifestationsPresent = formatsCount > 1;

    return (
      <div className="format-chooser-capsule-mobile">
        <FormatChooserCapsule
          availability={mainBibAvailability}
          bib={mainBib}
          className="capsule-mobile"
          displayChevron={multipleManifestationsPresent}
          formatsCount={formatsCount}
          handleClick={multipleManifestationsPresent ? this.handleOpen : undefined}
          href={multipleManifestationsPresent ? '#format-chooser-modal' : undefined}
          isActive
        />
        <FormatChooserModal
          catalogBib={catalogBib}
          summary={summary}
          handleClose={this.handleClose}
          handleClick={this.handleManifestationClick}
          manifestations={manifestations}
          realtimeAvailabilities={realtimeAvailabilities}
        />
      </div>
    );
  }

  renderDesktop() {
    const { realtimeAvailabilities, catalogBib, editions } = this.props;
    const renderCapsule = manifestation => {
      const isActive = manifestation.get('id') === catalogBib.get('id');
      const availability = realtimeAvailabilities.get(manifestation.get('id'), manifestation.get('availability'));

      return (
        <FormatChooserCapsule
          availability={availability}
          bib={manifestation}
          className="capsule-desktop"
          editions={editions}
          handleClick={!isActive ? () => this.handleManifestationClick(manifestation) : undefined}
          handleEditionClick={this.handleEditionClick}
          href={!isActive ? `/v2/record/${manifestation.get('id')}` : undefined}
          isActive={isActive}
          key={`format-chooser-capsule-desktop-${manifestation.get('id')}`}
        />
      );
    };

    return (
      <div className="format-chooser-capsule-desktop">
        {this.props.manifestations.slice(0, 3).map(renderCapsule)}
        {this.renderMoreFormatsDropdown()}
      </div>
    );
  }

  render() {
    return (
      <div className="cp-format-chooser">
        {this.renderDesktop()}
        {this.renderMobile()}
      </div>
    );
  }
}

FormatChooser.propTypes = {
  catalogBib: catalogBibShape.isRequired,
  editions: ImmutablePropTypes.listOf(bibShape).isRequired,
  intl: PropTypes.object.isRequired,
  manifestations: ImmutablePropTypes.listOf(bibShape).isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired,
  realtimeAvailabilities: ImmutablePropTypes.mapOf(availabilityShape).isRequired,
  summary: ImmutablePropTypes.map.isRequired,
  trackHoldingsExploreEdition: PropTypes.func.isRequired,
  trackHoldingsExploreFormat: PropTypes.func.isRequired
};

export default FormatChooser;
